import { logger } from "@rshbintech.dboul/logger";
/**
 * TODO: До момента появления собственного микросервиса аутентификации, для обеих версий API (монолит и микросервисы), мы используем монолитную версию интерфейса ответа логина (LoginResponse).
 * Убрать данный код когда появится микросервис аутентификации (http://sdlc.go.rshbank.ru/jira/browse/OB_COREUI-283).
 */
import { LoginRequest, LoginResponse } from "../api/monolith";
import { AccessToken, LoginPassReq } from "../api/authentication";
import { AuthService } from "../auth";
import {
  REFRESH_TOKEN_REQUEST_PATHNAME,
  GET_ACCESS_BY_REFRESH_TOKEN_PATHNAME,
} from "./constants";
import {
  isLoginRequest,
  isLoginOrOtpCheckRequest,
  isLoginOrCheckSMSCodeOrCheckCertificatetRequest,
  isLogoutRequest,
  isMsLoginRequest,
} from "./utils";
import {
  ApiConfig,
  ContentType,
  HttpResponse,
  RequestParams,
  QueryParamsType,
  FullRequestParams,
  QueryParamsPropertyType,
  QueryParamsArrayPropertyType,
} from "./types";

export class HttpClient<SecurityDataType = unknown> {
  public baseUrl?: string = process.env.API_URL;

  private securityData: SecurityDataType | null = null;

  private securityWorker?: ApiConfig<SecurityDataType>["securityWorker"];

  private customFetch = (...fetchParams: Parameters<typeof fetch>) =>
    fetch(...fetchParams);

  private baseApiParams: RequestParams = {
    credentials: "include", // TODO: Вернуть "same-origin" если мы когда-нибудь перестанем работать с third-party API
    headers: {
      "Content-Type": ContentType.Json,
    },
    redirect: "follow",
    referrerPolicy: "no-referrer",
  };

  constructor(apiConfig: ApiConfig<SecurityDataType> = {}) {
    Object.assign(this, apiConfig);
  }

  public setSecurityData = (data: SecurityDataType | null) => {
    this.securityData = data;
  };

  private encodeQueryParam(key: string, value: QueryParamsPropertyType) {
    const encodedKey = encodeURIComponent(key);

    return `${encodedKey}=${encodeURIComponent(
      typeof value === "number" ? value : `${value}`
    )}`;
  }

  private addQueryParam(query: QueryParamsType, key: string) {
    return this.encodeQueryParam(key, query[key]);
  }

  private addArrayQueryParam(
    query: QueryParamsType<QueryParamsArrayPropertyType>,
    key: string
  ) {
    const value = query[key];

    return value.map((v) => this.encodeQueryParam(key, v)).join("&");
  }

  protected toQueryString(rawQuery?: QueryParamsType): string {
    const query = rawQuery || {};
    const keys = Object.keys(query).filter(
      (key) => "undefined" !== typeof query[key]
    );

    return keys
      .map((key) =>
        Array.isArray(query[key])
          ? this.addArrayQueryParam(query, key)
          : this.addQueryParam(query, key)
      )
      .join("&");
  }

  protected addQueryParams(rawQuery?: QueryParamsType): string {
    const queryString = this.toQueryString(rawQuery);

    return queryString ? `?${queryString}` : "";
  }

  // Использование any связанно с высокой полиморфностью типа аргумента
  private contentFormatters = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [ContentType.Json]: (input: any) =>
      input !== null && (typeof input === "object" || typeof input === "string")
        ? JSON.stringify(input)
        : input,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [ContentType.FormData]: (input: any) =>
      Object.keys(input || {}).reduce((formData, key) => {
        const property = input[key];

        formData.append(
          key,
          property instanceof Blob
            ? property
            : typeof property === "object" && property !== null
            ? JSON.stringify(property)
            : `${property}`
        );

        return formData;
      }, new FormData()),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [ContentType.UrlEncoded]: (input: any) => this.toQueryString(input),
  };

  private mergeRequestParams(
    params1: RequestParams,
    params2?: RequestParams
  ): RequestParams {
    return {
      ...this.baseApiParams,
      ...params1,
      ...(params2 || {}),
      headers: {
        ...(this.baseApiParams.headers || {}),
        ...(params1.headers || {}),
        ...((params2 && params2.headers) || {}),
        ...(AuthService.hasAccessToken
          ? { Authorization: `Bearer ${AuthService.getAccessToken()}` }
          : {}),
      },
    };
  }

  public request = async <T = unknown, E = unknown>({
    body,
    secure,
    path,
    type,
    query,
    format,
    baseUrl,
    signal,
    ...params
  }: FullRequestParams): Promise<HttpResponse<T, E>> => {
    /**
     * INFO: Отменяем все запросы в очереди, если один из запросов получил ответ с кодом 401
     * и установил значение свойства AuthService.isNeedToCancelRequestsInQueue = true.
     */
    if (AuthService.isNeedToCancelRequestsInQueue) {
      return Promise.reject(new Error(`Request to ${path} was cancelled`));
    }

    try {
      /**
       * INFO: Для всех запросов кроме обновления токена (refresh-token),
       * в первую очередь проверяем необходимость обновления / получения access-токена.
       */
      if (
        AuthService.needUpdateAuthData &&
        path !== REFRESH_TOKEN_REQUEST_PATHNAME && // INFO: Запрос для обновления токена через Монолит
        path !== GET_ACCESS_BY_REFRESH_TOKEN_PATHNAME // INFO: Запрос для обновления токена через МС Аутентификации
      ) {
        await AuthService.updateAuthData();
      }
    } catch (error) {
      /**
       * TODO: Необходимо уточнить у дизайнеров как обрабатывать ошибку обновления токена на UI (http://sdlc.go.rshbank.ru/jira/browse/OB_COREUI-103).
       * Сейчас в случае любой ошибки (клиентской / серверной) мы редиректим пользователя на страницу логина, что может вызвать ложное представление о том что он разлогинен, хотя это не всегда так.
       */
      AuthService.resetAuthData();
      AuthService.redirectToLoginPage();

      /**
       * TODO: Обработку этого reject'а необходимо производить на уровне проекта и в случае 500-х ответов сервера отображать пользователю ErrorPage-компонент (http://sdlc.go.rshbank.ru/jira/browse/OB_COREUI-101).
       * Однако пока не уточним вопрос указанный в предыдущем комментарии, обработка этого reject'а не имеет смысла, поскольку сейчас при любой ошибке происходит редирект на страницу логина.
       */
      return Promise.reject(error);
    }

    const secureParams =
      ((typeof secure === "boolean" ? secure : this.baseApiParams.secure) &&
        this.securityWorker &&
        (await this.securityWorker(this.securityData))) ||
      {};

    const requestParams = this.mergeRequestParams(params, secureParams);
    const queryString = query && this.toQueryString(query);
    const payloadFormatter = this.contentFormatters[type || ContentType.Json];
    const responseFormat = format || requestParams.format;

    const requestInit: RequestInit = {
      ...requestParams,
      headers: {
        ...(type ? { "Content-Type": type } : {}),
        ...(requestParams.headers || {}),
      },
      signal,
      body:
        typeof body === "undefined" || body === null
          ? null
          : payloadFormatter(body),
    };

    if (type === ContentType.FormData) {
      // Headers без заголовка content-type нужен для того что-бы браузер сам мог проставить WebKitFormBoundary при отправке multipart/form-data
      // Подробнее - https://gitlab.rshbdev.ru/rshbintech/crft/dboul/ndbo/frontend/rshb/http/-/merge_requests/87
      const headers = new Headers(requestInit.headers);

      headers.delete("Content-Type");
      requestInit.headers = headers;
    }

    return this.customFetch(
      `${baseUrl || this.baseUrl || ""}${path}${
        queryString ? `?${queryString}` : ""
      }`,
      requestInit
    ).then(async (response) => {
      const r = response as HttpResponse<T, E>;

      r.data = null as unknown as T;
      r.error = null as unknown as E;

      const data = !responseFormat
        ? r
        : await response[responseFormat]()
            .then((data) => {
              if (r.ok) {
                r.data = data;
              } else {
                r.error = data;
              }

              return r;
            })
            .catch((e) => {
              r.error = e;

              return r;
            });

      /**
       * INFO: В случае получения ошибки 401 на любом запросе
       * устанавливаем параметр редиректа для возврата на целевую страницу после повторного входа,
       * очищаем auth-данные и показываем пользователю страницу логаута
       */
      if (response.status === 401) {
        logger.sendError(new Error("401 Auth error"), {
          extra: {
            path,
            hasAccessToken: AuthService.hasAccessToken,
            hasRefreshToken: AuthService.hasRefreshToken,
            hasCurrentAuthZone: AuthService.hasCurrentAuthZone,
            exp: AuthService.getTokenIssuerInfo()?.exp,
            browserTimeInSeconds: Math.floor(Date.now() / 1000),
            monolithClientId:
              AuthService.getTokenIssuerInfo()?.monolithClientId,
          },
        });
        AuthService.setLoginPageNextParam(window.location.pathname);
        AuthService.resetAuthData();
      }

      /**
       * INFO: Нам необходимо минимизировать любые манипуляции с токенами в проектах,
       * поэтому инкапсулируем всю логику работы с ними на уровне http-клиента и auth-сервиса.
       *
       * В данном условии проверяем отправку запросов в МС Аутентификации.
       */
      if (
        isLoginOrCheckSMSCodeOrCheckCertificatetRequest(path) &&
        response.ok
      ) {
        const loginResponse = data.data as unknown as AccessToken;

        if (isMsLoginRequest(path)) {
          AuthService.setUserCredentials(
            {
              login: (body as LoginPassReq).login,
              password: (body as LoginPassReq).password,
            },
            true
          );
        }

        if (loginResponse.access_token) {
          await AuthService.getSsoRefreshTokenByLoginPassword(
            loginResponse.access_token
          );
          await AuthService.setAccessToken(loginResponse.access_token);

          loginResponse.access_token = "";
        }
      }

      /**
       * INFO: Нам необходимо минимизировать любые манипуляции с токенами в проектах,
       * поэтому инкапсулируем всю логику работы с ними на уровне http-клиента и auth-сервиса.
       *
       * В данном условии проверяем отправку запросов в Монолит.
       */
      if (isLoginOrOtpCheckRequest(path) && response.ok) {
        const loginResponse = data.data as unknown as LoginResponse;

        if (isLoginRequest(path)) {
          AuthService.setUserCredentials(
            {
              login: (body as LoginRequest).login,
              password: (body as LoginRequest).password,
            },
            false
          );
        }

        if (loginResponse.jwt) {
          await AuthService.setAccessToken(loginResponse.jwt);

          // Для ограничения прямого доступа, удаляем токен из объекта ответа
          loginResponse.jwt = "";
        }
      }

      /**
       * INFO: Проверяем что получили ответ на запрос логаута
       * и в случае успешного ответа очищаем auth-данные и показываем пользователю страницу логаута.
       */
      if (isLogoutRequest(path) && response.ok) {
        AuthService.resetAuthData();
      }

      if (!response.ok) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw data;
      }

      return data;
    });
  };
}
